import { createSelector } from 'reselect';

import { StateDictionary } from './utils/state-manager';

type AnnouncementDictionary = { [orgId: number]: Array<AnnouncementSummary> };
type AnnouncementDeliverySummaryDictionary = {
  [announcementId: number]: AnnouncementDeliverySummary;
};
type JobApplicantMessageDictionary = { [messageId: number]: JobApplicantJobMessage };
type EmployeeMessageDictionary = { [orgId: number]: Array<EmployeeOrgMessage> };

export type MessagesState = {
  messagesById: JobApplicantMessageDictionary;
  messagesStateByOrgId: StateDictionary;
  messagesStateByJobId: StateDictionary;
  announcementsByOrgId: AnnouncementDictionary;
  announcementsByOrgIdStatus: StateDictionary;
  announcementDeliverySummaryById: AnnouncementDeliverySummaryDictionary;
  announcementDeliverySummaryByIdStatus: StateDictionary;
  messagesByJobIdStatus: StateDictionary;
  employeeMessagesByOrgId: EmployeeMessageDictionary;
  employeeMessagesByOrgIdStatus: StateDictionary;
};

type State = { messages: MessagesState };

const announcementsByOrgIdSelector = (state: State) => state.messages.announcementsByOrgId;
const announcementsByOrgIdStatusSelector = (state: State) =>
  state.messages.announcementsByOrgIdStatus;
export const announcementsForOrgIdSelector = createSelector(
  [announcementsByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (announcementsByOrgId, orgId) => announcementsByOrgId[orgId],
);
export const announcementsForOrgIdStatusSelector = createSelector(
  [announcementsByOrgIdStatusSelector, (_state: State, orgId: number) => orgId],
  (announcementsByOrgIdStatus, orgId) => announcementsByOrgIdStatus[orgId],
);

const announcementDeliverySummaryByIdSelector = (state: State) =>
  state.messages.announcementDeliverySummaryById;
export const announcementDeliverySummaryForIdSelector = createSelector(
  [
    announcementDeliverySummaryByIdSelector,
    (_state: State, announcementId: number) => announcementId,
  ],
  (summariesById, announcementId) => summariesById[announcementId],
);
export const announcementDeliverySummaryForOrgIdSelector = createSelector(
  [announcementDeliverySummaryByIdSelector, (_state: State, orgId: number) => orgId],
  (summariesById, orgId) =>
    Object.values(summariesById).filter((summary) => summary.orgId === orgId),
);
const announcementDeliverySummaryStateByIdSelector = (state: State) =>
  state.messages.announcementDeliverySummaryByIdStatus;
export const announcementDeliverySummaryStateForIdSelector = createSelector(
  [
    announcementDeliverySummaryStateByIdSelector,
    (_state: State, announcementId: number) => announcementId,
  ],
  (summariesStateById, announcementId) => summariesStateById[announcementId],
);

const employeeMessagesByOrgIdSelector = (state: State) => state.messages.employeeMessagesByOrgId;
export const employeeMessagesForOrgIdSelector = createSelector(
  [employeeMessagesByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (employeeMessagesByOrgId, orgId) => employeeMessagesByOrgId[orgId],
);

const employeeMessagesByOrgIdStatusSelector = (state: State) =>
  state.messages.employeeMessagesByOrgIdStatus;
export const employeeMessagesForOrgIdStatusSelector = createSelector(
  [employeeMessagesByOrgIdStatusSelector, (_state: State, orgId: number) => orgId],
  (employeeMessagesByOrgIdStatus, orgId) => employeeMessagesByOrgIdStatus[orgId],
);

const messagesByIdSelector = (state: State) => state.messages.messagesById;
export const messagesForJobIdSelector = createSelector(
  [messagesByIdSelector, (_state: State, jobId: number) => jobId],
  (messagesById, jobId) => Object.values(messagesById).filter((m) => m.jobId === jobId),
);
export const activeJobMessagesForOrgId = createSelector(
  [messagesByIdSelector, (_state: State, orgId: number) => orgId],
  (messagesById, orgId) =>
    Object.values(messagesById).filter((m) => m.orgId === orgId && m.jobIsActive),
);
export const jobMessagesForOrgId = createSelector(
  [messagesByIdSelector, (_state: State, orgId: number) => orgId],
  (messagesById, orgId) => Object.values(messagesById).filter((m) => m.orgId === orgId),
);

const messagesStateByJobIdSelector = (state: State) => state.messages.messagesStateByJobId;
export const messagesStateForJobIdSelector = createSelector(
  [messagesStateByJobIdSelector, (_state: State, jobId: number) => jobId],
  (messagesStateByJobId, jobId) => messagesStateByJobId[jobId],
);

const messagesStateByOrgIdSelector = (state: State) => state.messages.messagesStateByOrgId;
export const messagesStateForOrgIdSelector = createSelector(
  [messagesStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (messagesStateByOrgId, orgId) => messagesStateByOrgId[orgId],
);
